import type { WalletAddress, } from "@bracket/types";
import { GRAPHQL_ENDPOINT, } from "./const";




export declare type RecordErrorProps = {
	error: Error | AggregateError;
	address?: WalletAddress;
	system?: string;
	code?: string;
	stack?: string;
	data?: string;
};




/**
 * Records an error by sending a GraphQL mutation to a predefined endpoint.
 *
 * @param {RecordErrorProps} params - Parameters for recording the error
 * @param {Error | AggregateError} params.error - The error object (or AggregateError) to be recorded
 * @param {WalletAddress} [params.address] - Optional associated wallet address, used to set request header and as GQL variable
 * @param {string} [params.system] - Optional system identifier
 * @param {string} [params.code] - Optional error code or reason for the error
 * @param {string} [params.stack] - Optional stack trace (uses error.stack by default)
 * @param {string} [params.data] - Optional additional data to record with the error
 * @returns {Promise<boolean>} - Resolves true on success, false on error or GraphQL error
 */
export const recordError = async ({
	error,
	address,
	system,
	code,
	stack,
	data,
}: RecordErrorProps): Promise<boolean> => {
	const headers: HeadersInit = {
		"Content-Type": "application/json",
		...(address ? { wallet: address, } : {}),
	};
	const query = `
		mutation record_ui_error($system: String, $code: String, $address: String, $message: String, $stack: String, $data: String) {
			record_ui_error(system: $system, code: $code, address: $address, message: $message, stack: $stack, data: $data) {
				success
			}
		}`;
	const variables = {
		system: system || "UNKNOWN",
		code: code || error.name,
		address: address || "",
		message: error.message,
		stack: JSON.stringify(stack ?? error.stack ?? ""),
		data: data || null,
	};
	const response = await fetch(GRAPHQL_ENDPOINT, { method: "POST", body: JSON.stringify({ query, variables, }), headers, });
	if (response.ok) {
		const result = await response.json();
		if (result.errors) {
			console.warn(`@bracket/catch: failed recording error via GQL: received: ${JSON.stringify(result.errors)}`);
			return false;
		}
		return result.data.record_ui_error.success;
	}
	console.warn(`@bracket/catch: failed recording error via GQL: response from endpoint: ${GRAPHQL_ENDPOINT} not ok: ${response.statusText}`);
	return false;
};
